import { useState } from "react";
import type { VideoItem } from "@/types/video";

interface EpisodeListProps {
  video: VideoItem;
}

const PER_PAGE = 30;

export default function EpisodeList({ video }: EpisodeListProps) {
  const total = video.episodeCount;
  const [selected, setSelected] = useState(1);
  const [page, setPage] = useState(1);

  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));
  const start = (page - 1) * PER_PAGE + 1;
  const end = Math.min(total, page * PER_PAGE);
  const episodes = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const unit = video.type === "综艺" ? "期" : "集";
  const labelPrefix = video.type === "综艺" ? "第" : "第";

  const selectEpisode = (n: number) => {
    setSelected(n);
    const panel = document.getElementById("episode-panel");
    if (panel && window.innerWidth < 1024) {
      panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  return (
    <section className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-base font-semibold text-foreground-950">
          {video.type === "综艺" ? "节目期数" : "剧集列表"}
        </h2>
        <span className="font-label text-xs text-foreground-500">共 {total} {unit}</span>
      </div>

      {pageCount > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPage(p)}
              className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                p === page
                  ? "border-primary-500 bg-primary-500/15 text-primary-400"
                  : "border-background-200 text-foreground-600 hover:border-primary-500/60"
              }`}
            >
              {`${(p - 1) * PER_PAGE + 1}-${Math.min(total, p * PER_PAGE)}`}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10">
        {episodes.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => selectEpisode(n)}
            className={`flex h-9 items-center justify-center rounded-md border font-label text-xs transition-colors ${
              n === selected
                ? "border-primary-500 bg-primary-500 text-background-50"
                : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-500/60 hover:text-primary-400"
            }`}
          >
            {n}
          </button>
        ))}
      </div>

      <div id="episode-panel" className="mt-4 rounded-md border border-background-200 bg-background-50 p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-7 items-center rounded-md bg-accent-500/15 px-2 font-label text-xs font-semibold text-accent-400">
            {labelPrefix}{selected}{unit}
          </span>
          <span className="text-sm font-medium text-foreground-900">
            {video.title} · {labelPrefix}{selected}{unit}
          </span>
        </div>
        <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
          本{unit}继续推进主线剧情，人物关系与矛盾进一步展开，为后续篇章埋下伏笔。
        </p>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-foreground-500">
          <i className="ri-information-line"></i>
          当前为章节内容说明演示，本站不提供真实的视频播放或下载功能。
        </p>
      </div>
    </section>
  );
}